import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { DrawerLayout } from '@/layouts/DrawerLayout';
import { FormProvider, SubmitHandler, useForm, useFormContext, useWatch } from 'react-hook-form';
import { AlertTitle, Alert, Box, Container, Divider, Theme, useMediaQuery, Typography, Button, Accordion, AccordionSummary, AccordionDetails, Tooltip, Stack } from '@mui/material';
import Grid from '@mui/material/Grid2';
import DrawerHeader from '@/components/Organisms/BasicDrawer/DrawerHeader.component';
import WorkflowRuleStep2 from './WorkflowRuleStep2.component';
import WorkflowRuleStep3 from './WorkflowRuleStep3.component';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createAllWorkflowRulesQueryOptions } from '@/services/workflowRules/workflowRules.options';
import { FieldData, initialWorkflowRule, NewCondition, OldAssigneeAction, OldStatusAction, OldCondition, OldEmailAction, WorkflowRule, workflowRuleSchema, NewConditionNESTED, Conditions, NewConditionFLAT } from './workflowRules.interface';
import { Customer } from '@/interfaces/schemas/customer.schema';
import { NewWorkflowRuleAPI, OldWorkflowRuleAPI } from '@/services/workflowRules/workflowRules.service';
import { Equality, EqualityDisplayValueArray, EqualityNumber, turnEqualityValueToNumber, turnOldNumberToEqualityValue } from '@/constants/filter/filter.constant';
import { createStatusesByCustomerOptions, createTicketTypesByCustomerOptions } from '@/services/ticket/ticketType.options';
import { CFTypeIdDetail } from '../workflowRules/useWorkflowRulesAdminTable.hook';
import { createCustomerOptionsQuery } from '@/services/customer/customer.options';
import { createBaseFieldsQueryOptions, createCustomerFieldsQueryOptions } from '@/services/ticket/ticket.options';
import { AutoComplete } from '@utilisourcepackagelibdev/utilisourcepackagelib';
import TextBox from '@/components/Molecules/Textbox/Textbox.component';
import { LoadingButton } from '@mui/lab';
import { Trash2Icon } from 'lucide-react';
import ConfirmModal from '@/components/Molecules/Modal/ConfirmModal.component';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { createWorkflowAgentByCustomerQuery } from '@/services/agent/agent.options';
import useWorkflowRuleMutation from '@/services/workflowRules/workflowRules.mutation';
import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';

interface StatusByCustomer {
  StatusID: number;
  Name: string;
  Color: number;
  Flags: number;
}

const WorkflowRuleDrawer = () => {
  const navigate = useNavigate();
  const params = useParams({ from: '/_auth/workflow-rules/$workflowruleId' });
  const { workflowruleId } = params;
  const { variant, step } = useSearch({ from: '/_auth/workflow-rules/$workflowruleId' })

  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const [isOpen, setIsOpen] = useState(false)
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openCustomerChangeModal, setOpenCustomerChangeModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const methods = useForm<WorkflowRule>({
    mode: 'all',
    defaultValues: initialWorkflowRule,
    shouldUnregister: false,
    // resolver: zodResolver(workflowRuleSchema)
  });

  // Query workflow rule data based on ID
  const workflowRule = useQuery(createAllWorkflowRulesQueryOptions())
  const customerIdWatch = useWatch({ control: methods.control, name: 'customer.id' })

  // use params to get the associated workflow rule
  // Find either existing rule or new rule based on ID
  const currentWorkflowRule: OldWorkflowRuleAPI | NewWorkflowRuleAPI | undefined = workflowRule.data?.find(
    (rule: any) => rule.SmartRuleId === workflowruleId
  );

  const customers = useQuery(createCustomerOptionsQuery())
  const baseFields = useQuery(createBaseFieldsQueryOptions())

  const customFields = useSuspenseQuery(createCustomerFieldsQueryOptions(currentWorkflowRule?.Customer))
  const ticketTypes = useSuspenseQuery(createTicketTypesByCustomerOptions({ customerId: currentWorkflowRule?.Customer }))
  const statuses = useSuspenseQuery(createStatusesByCustomerOptions({ customerId: currentWorkflowRule?.Customer }))
  const agents = useSuspenseQuery(createWorkflowAgentByCustomerQuery({ customerId: currentWorkflowRule?.Customer }))

  const { workflowRuleMutation } = useWorkflowRuleMutation()

  const handleClose = () => {
    setIsOpen(false)
    // Wait for animation to complete before navigating
    setTimeout(() => {
      navigate({ to: '/workflow-rules' })
    }, 300) // Match this with MUI's transition duration
  }

  const handleCloseDrawer = () => {
    handleClose()
    // methods.reset(editCustomerValues2);
    navigate({ to: '/workflow-rules' });
  };

  const handleSubmit: SubmitHandler<any> = (formValues: any) => {
    console.log('formValues : ', formValues);

    console.log(mapConditionsForAPI(formValues.any_conditions))
    if (variant === 'edit') {
      console.log('edit')
      handleEditWorkflowRule(formValues);
    } else {
      console.log('create')
      handleCreateWorkflowRule(formValues);
    }
    return undefined;
  }

  const handleEditWorkflowRule = (formValues: any) => {
    console.log(formValues);
    console.log(mapConditionsForAPI(formValues.any_conditions))
    workflowRuleMutation.mutate({
      actionType: 'updateWorkflowRule',
      payload: {
        SmartRuleID: workflowruleId,
        Customer: formValues.customer.id,
        Description: formValues.description,
        Actions: mapActionsForAPI(formValues.actions),
        AnyConditions: mapConditionsForAPI(formValues.any_conditions),
        AllConditions: mapConditionsForAPI(formValues.all_conditions),
        ExecCount: formValues.execCount,
      },
      data: formValues
    });
  }

  const handleCreateWorkflowRule = (formValues: any) => {
    console.log('createWorkflowRule');
    console.log(formValues);
    console.log(mapConditionsForAPI(formValues.any_conditions))
    workflowRuleMutation.mutate({
      actionType: 'createWorkflowRule',
      payload: {
        Customer: formValues.customer.id,
        Description: formValues.description,
        Actions: mapActionsForAPI(formValues.actions),
        AnyConditions: mapConditionsForAPI(formValues.any_conditions),
        AllConditions: mapConditionsForAPI(formValues.all_conditions),
        ExecCount: formValues.execCount,
      },
      data: formValues
    });
  }

  // type RequestCondition = {
  //   fieldTypeId: number;
  //   conditionId: number;
  //   value: string[];
  //   type: string;
  //   column: string;
  //   equality: number;
  //   fieldId: number;
  //   userId: number | null;
  //   statusValue: string[] | null;
  // }

  const mapConditionsForAPI = (conditions: any[]): NewConditionFLAT[] => {
    console.log(conditions)

    const newValue = (value: string[], fieldTypeId: number, equality: number) => {

      console.log(value, fieldTypeId, equality)

      if (equality === EqualityNumber.isBetween && fieldTypeId === 22) {
        console.log('isBetween')
        const start = dayjs(value.start).utc().format()
        const end = dayjs(value.end).utc().format()
        return [start, end]
        // if the fieldTypeId is 22 then return the value as a date
      } else if (fieldTypeId === 22) {
        return [dayjs(value[0]).utc().format()]
      } else if (fieldTypeId === 16 && equality === EqualityNumber.isBetween && Array.isArray(value) && value.length === 2) { // integer
        return [(value[0]), (value[1])]
      } else {
        // If value is already an array, return it as is, otherwise wrap it in an array
        return Array.isArray(value) ? value : [value];
      }
    }

    return conditions.map((condition: any): NewConditionFLAT => ({
      fieldTypeId: condition.conditionObj.TypeId || 0,
      conditionId: condition.conditionId || 0,
      // Handle date range for "is between" equality (4)
      value: newValue(condition.value, condition.fieldTypeId, condition.equality),
      //  condition.equality === EqualityNumber.isBetween && condition.value?.start && condition.value?.end
      //   ? [
      //     dayjs(condition.value.start).utc().toISOString(), // is between dates
      //     dayjs(condition.value.end).utc().toISOString()
      //   ]
      //   : condition.conditionObj?.Value?.TimeVal !== undefined && condition.value
      //     ? [dayjs(condition.value).utc().toISOString()] // any other date value
      //     : [condition.value?.toString() || ''], // any other value

      type: condition.type || '',
      column: condition.column || '',
      equality: (condition.equality) || 0,
      fieldId: condition.conditionObj.Meta.Cid || 0,
      // userId: condition?.userId || null,
      statusValue: condition?.statusValue || null,
    }));
  }

  const mapActionsForAPI = (actions: any): any[] => {
    console.log(actions)
    const FieldType = {
      'status': 2,    // ActSetStatus = 2
      'assignee': 3,  // ActSetAssignee = 3
      'email': 1      // ActEmail = 1
    };

    // Create an array to hold the actions that have their switches turned on
    const actionArray = [];

    // Check if status action is enabled and add it to the array
    if (actions.status?.status_switch) {
      actionArray.push({
        Actiontype: FieldType['status'],
        Actiondata: { StatusId: actions.status.status_config.statusObject.id }
      });
    }

    // Check if assignee action is enabled and add it to the array
    if (actions.assignee?.assignee_switch) {
      actionArray.push({
        Actiontype: FieldType['assignee'],
        Actiondata: { Assignee: actions.assignee.assignee_config.assigneeObject.id }
      });
    }

    // Check if email action is enabled and add it to the array
    if (actions.email?.email_switch) {
      actionArray.push({
        Actiontype: FieldType['email'],
        Actiondata: {
          Recipient: actions.email.email_config.Recipient,
          // Process recipient list to handle both email addresses and agent IDs
          // If the label contains '@', use the label (email address), otherwise use the ID (agent)
          RecipientList: actions.email.email_config.RecipientList.map((recipient: any) =>
            recipient.label && recipient.label.includes('@') ? recipient.label : recipient.id.toString()
          ) || [],
          // RecipientList: actions.email.email_config.RecipientList.map((recipient: any) => recipient.id) || [],
          Subject: actions.email.email_config.Subject || '',
          Body: actions.email.email_config.Body || ''
        }
      });
    }

    console.log(actionArray)
    return actionArray
  }

  const handleDelete = () => {
    console.log('handleDelete')
    setIsDeleting(true);
    workflowRuleMutation.mutate({
      actionType: 'deleteWorkflowRule',
      payload: {
        id: workflowruleId
      },
    })
  };

  const turnNumberToEqualityValue = ({ numberValue, showDisplayValue, isOldRule }: { numberValue: number, showDisplayValue: boolean, isOldRule: boolean }): string => {

    let equalityMap: { [key: number]: { raw: string; display: string } } = {}

    if (isOldRule) {
      equalityMap = {
        0: { raw: "isNotEqual", display: "Is Not Equal" },
        1: { raw: "isEqual", display: "Is Equal" },
      }

      return showDisplayValue ? equalityMap[numberValue]?.display : equalityMap[numberValue]?.raw;
    }

    // Return either raw equality value or display value based on showDisplayValue param
    equalityMap = {
      0: { raw: "isEqual", display: "Is Equal" },
      1: { raw: "isNotEqual", display: "Is Not Equal" },
      2: { raw: "greaterThan", display: "Greater Than" },
      3: { raw: "lessThan", display: "Less Than" },
      4: { raw: "isBetween", display: "Is Between" },
      5: { raw: "isSet", display: "Is Set" },
      6: { raw: "isNotSet", display: "Is Not Set" },
      7: { raw: "today", display: "Today" },
      8: { raw: "onOrBefore", display: "On Or Before" },
      9: { raw: "onOrAfter", display: "On Or After" },
      10: { raw: "withinTheLast", display: "Within The Last" },
      11: { raw: "withinTheNext", display: "Within The Next" },
      12: { raw: "anyValue", display: "Any Value" },
      13: { raw: "contains", display: "Contains" }
    };

    // Return default if number is out of range
    if (numberValue < 0 || numberValue >= Object.keys(equalityMap).length) {
      return showDisplayValue ? "Is Equal" : "isEqual";
    }

    return showDisplayValue ? equalityMap[numberValue]?.display : equalityMap[numberValue]?.raw;
  }

  // Helper function to map field types to field names
  const findFieldName = (input: FieldData | undefined) => {
    const fieldMapping: { [key: number]: string } = {
      // Mapping field type numbers to field names
      1: "Status",
      3: "Assignee",
      4: "RaisedBy",
      5: "Type",

    };

    const fieldName = fieldMapping[input?.Fieldtype || 0];
    return fieldName;
  }

  // Enum for field type mapping
  enum FieldType {
    status = 1,
    assignee = 3,
    raisedby = 4,
    type = 5
  }

  // Helper function to find workflow rule value based on field type
  const findWorkflowRuleByFieldType = useCallback((input: FieldData | undefined) => {
    const fieldName = findFieldName(input);

    if (!fieldName) {
      console.error("Invalid Fieldtype");
      return null;
    }

    if (fieldName === "Type") {
      console.log(input?.FieldString)

      if (!ticketTypes.data) {
        console.log("Ticket types data not yet loaded");
        return "Loading..."; // Or return a default/fallback value
      }

      console.log(ticketTypes.data)
      console.log(ticketTypes.data?.find(
        (type: any) => type.TypeId === input?.FieldString
      )?.Name)
      return ticketTypes.data?.find(
        (type: any) => type.TypeId === input?.FieldString
      )?.Name;
    }

    if (fieldName === "Status") {
      console.warn('STATUS WARNING')
      console.log(input?.FieldString)
      console.log(statuses.data)

      const selectedStatus = statuses.data?.find(
        (status: StatusByCustomer) => status.StatusID === input?.FieldString
      );


      console.log(selectedStatus)
      // +1 because of the placeholder value of Select Status is index 0 
      return selectedStatus?.Name

    }

    if (fieldName === "Assignee" || fieldName === "RaisedBy") {
      console.log(agents?.data?.find(
        (agent: any) => agent.Agentid === input?.FieldString
      ).Name)
      return agents?.data?.find(
        (agent: any) => agent.Agentid === input?.FieldString
      ).Name;
    }


    return null;
  }, [ticketTypes.data, statuses.data, agents.data])

  // Transform workflow rule data to match schema format
  const transformEmailRecipient = ({ Actiondata, agents }: any) => {
    const result: { id: number; label: string }[] = [];

    // Handle RecipientList
    if (Array.isArray(Actiondata.RecipientList) && Actiondata.RecipientList.length > 0) {
      const listResults = Actiondata.RecipientList.map((recipient: string) => {
        // Handle email strings
        if (recipient.includes('@')) {
          return {
            id: recipient,
            label: recipient
          };
        } else {
          const recipientId = agents?.data?.find(
            (agent: any) => agent.Agentid === Number(recipient)
          )

          const label = agents?.data?.find(
            (agent: any) => agent.Agentid === Number(recipient)
          )?.Name

          return {
            id: Number(recipient),
            label: label
          }
        }
      });

      result.push(...listResults);
    }

    // Handle single Recipient
    if (Actiondata.Recipient && Actiondata.Recipient > 0) {
      const agentName = agents.data?.find(
        (agent: any) => agent.Agentid === Actiondata.Recipient
      )?.Name || '';

      result.push({
        id: Actiondata.Recipient,
        label: agentName
      });
    }

    return result;
  };

  const renderDynamicFieldValue = (customField: any, oldC: OldCondition) => {
    if (customField && customField?.TypeId && CFTypeIdDetail(customField?.TypeId).ClassBinding) {
      switch (CFTypeIdDetail(customField.TypeId).ClassBinding) {
        case 'cfTDropdown': return oldC.FieldData?.FieldString && typeof oldC.FieldData.FieldString === 'object' ? oldC.FieldData.FieldString.Json?.Value?.SelectedOpt : undefined;
        case 'cfTDate': return oldC.FieldData?.FieldString && typeof oldC.FieldData.FieldString === 'object' ? oldC.FieldData.FieldString.Json?.Value?.IncludeTime : undefined;
        case 'cfTString': return oldC.FieldData?.FieldString && typeof oldC.FieldData.FieldString === 'object' ? oldC.FieldData.FieldString.Json?.Value?.Str : undefined;
        case 'cfTInteger': return oldC.FieldData?.FieldString && typeof oldC.FieldData.FieldString === 'object' ? oldC.FieldData.FieldString.Json?.Value : undefined;
        case 'cfTToggle': return oldC.FieldData?.FieldString && typeof oldC.FieldData.FieldString === 'object' ? oldC.FieldData.FieldString.Json?.Value?.Checked : undefined;
        default: return []
      }
    }
  }

  const mapBaseFieldCondition = (oldC: OldCondition, conditionObject: any) => {
    let condition = {
      fieldTypeId: oldC.FieldData?.Fieldtype,
      conditionId: oldC.FieldData?.FieldString && typeof oldC.FieldData.FieldString === 'object' ? oldC.FieldData.FieldString.Cid : undefined,
      userId: null,
      statusValue: null,
      column: findFieldName(oldC.FieldData) || '',
      type: oldC.FieldData?.Fieldtype > 10 ? 'customField' : 'baseField',
      equality: Number(oldC.Equality) || 0,
      equalityDisplay: turnNumberToEqualityValue({ numberValue: oldC.Equality, showDisplayValue: true, isOldRule: true }) || '',
      value: findWorkflowRuleByFieldType(oldC.FieldData) || '',
      conditionObj: conditionObject || {},
      // condition: findFieldName(oldC.FieldData) || '',
      // fieldId: oldC.FieldData?.FieldString?.Cid,
      // equality: turnNumberToEqualityValue({ numberValue: oldC.Equality, showDisplayValue: true, isOldRule: true }) || '',
      // type: oldC.FieldData?.Fieldtype > 10 ? 'customField' : 'baseField',
      // allAny: 'Any'
    };

    if (conditionObject?.Meta?.Cid === 9000) {
      condition = {
        ...condition,
        conditionObj: {
          ...conditionObject,
          Type: {
            ...conditionObject.Type,
            OptLabels: ticketTypes?.data?.map((item: any) => item.Name) || [],
            UseOption: true
          }
        },
      }
    }

    return condition;
  }

  const mapCustomFieldCondition = (oldC: OldCondition) => {
    const currentCustomField = customFields?.data?.find((item: any) =>
      item?.Meta?.Cid === (oldC.FieldData?.FieldString && typeof oldC.FieldData.FieldString === 'object' ?
        oldC.FieldData.FieldString.Cid : undefined)
    )

    return {
      fieldTypeId: oldC.FieldData?.Fieldtype,
      conditionId: oldC.FieldData?.FieldString && typeof oldC.FieldData.FieldString === 'object' ? oldC.FieldData.FieldString.Cid : undefined,
      userId: null,
      statusValue: null,
      column: currentCustomField?.Meta?.Name || '',
      type: oldC.FieldData?.Fieldtype > 10 ? 'customField' : 'baseField',
      equality: Number(oldC.Equality) || 0,
      equalityDisplay: turnNumberToEqualityValue({ numberValue: oldC.Equality, showDisplayValue: true, isOldRule: true }) || '',
      value: renderDynamicFieldValue(currentCustomField, oldC) || '',
      conditionObj: currentCustomField || {},
      // condition: currentCustomField?.Meta?.Name || '',
      // equality: turnNumberToEqualityValue({ numberValue: oldC.Equality, showDisplayValue: true, isOldRule: true }) || '',
      // equalityNumber: Number(oldC.Equality) || 0,
      // value: renderDynamicFieldValue(currentCustomField, oldC) || '',
      // conditionObj: currentCustomField || {},
      // type: oldC.FieldData?.Fieldtype > 10 ? 'customField' : 'baseField',
      // allAny: 'All'
    };
  }

  const mapActions = (actions: OldEmailAction[] | OldAssigneeAction[] | OldStatusAction[]) => {

    console.log(actions)

    // Transform actions into an object with specific keys based on action type
    // This ensures they register in react-hook-form as actions.email, actions.status, etc.
    const transformedActions: any = {};

    actions.forEach((action: OldEmailAction | OldAssigneeAction | OldStatusAction) => {
      if (action.Actiontype === 1) { // Email action
        transformedActions.email = {
          type: 'email',
          email_switch: true,
          email_config: {
            Recipient: action.Actiondata.Recipient,
            RecipientList: transformEmailRecipient({ Actiondata: action.Actiondata, agents: agents }),
            Subject: action.Actiondata.Subject,
            Body: action.Actiondata.Body,
          }
        };
      } else if (action.Actiontype === 2) { // Status action
        transformedActions.status = {
          type: 'status',
          status_switch: true,
          status_config: {
            statusObject: {
              id: action.Actiondata.StatusId,
              label: statuses.data?.find((status: StatusByCustomer) => status.StatusID === action.Actiondata.StatusId)?.Name || ''
            }
          }
        };
      } else if (action.Actiontype === 3) { // Assignee action
        transformedActions.assignee = {
          type: 'assignee',
          assignee_switch: true,
          assignee_config: {
            assigneeObject: {
              id: action.Actiondata.Assignee,
              label: agents.data?.find((agent: any) => agent.Agentid === action.Actiondata.Assignee)?.Name || ''
            }
          }
        };
      }
    });
    console.log(transformedActions)
    return transformedActions;



    // console.warn(action)
    // if (action.Actiontype) {
    //   return mapOldAction(action, index)
    // } else {
    //   return mapNewAction(action, index)
    // }
  }

  // const mapAction = (action: OldEmailAction | OldAssigneeAction | OldStatusAction, index: number) => {
  //   console.warn(action)
  //   if (action.Actiontype) {
  //     return mapOldAction(action, index)
  //   } else {
  //     return mapNewAction(action, index)
  //   }
  // }

  // const mapNewAction = (action: any, index: number) => {
  //   console.warn("NEW ACTION")
  // }

  // const mapOldAction = (action: OldEmailAction | OldAssigneeAction | OldStatusAction, index: number) => {
  //   console.warn("OLD ACTION")
  //   console.log(index)
  //   switch (action.Actiontype) {
  //     case 1:
  //       return {
  //         type: 'email',
  //         email_switch: true,
  //         email_config: {
  //           Recipient: action.Actiondata.Recipient,
  //           RecipientList: transformEmailRecipient({ Actiondata: action.Actiondata, agents: agents }),
  //           Subject: action.Actiondata.Subject,
  //           Body: action.Actiondata.Body,
  //         }
  //       }
  //     case 2:
  //       return {
  //         type: 'status',
  //         status_switch: true,
  //         status_config: {
  //           // StatusId: action.Actiondata.StatusId,
  //           statusObject: {
  //             id: action.Actiondata.StatusId,
  //             label: statuses.data?.find((status: StatusByCustomer) => status.StatusID === action.Actiondata.StatusId)?.Name || ''
  //           }
  //         }
  //       }
  //     case 3:
  //       return {
  //         type: 'assignee',
  //         assignee_switch: true,
  //         assignee_config: {
  //           // Assignee: action.Actiondata.Assignee,
  //           assigneeObject: {
  //             id: action.Actiondata.Assignee,
  //             label: agents.data?.find((agent: any) => agent.Agentid === action.Actiondata.Assignee)?.Name || ''
  //           }
  //         }
  //       }
  //   }
  // }

  const mapCondition = (c: NewCondition | OldCondition | NewConditionFLAT) => {
    // Error handling for condition object
    if (!c) {
      console.error('Invalid condition object:', c);
      return null
    }

    // Check if the condition is an old condition by checking for Fieldtype property
    // @ts-ignore
    const isOldCondition = c?.FieldData && 'Fieldtype' in c?.FieldData;

    // if it is a new condition
    if (!isOldCondition) {
      console.log('New Condition')
      const newC = c as NewConditionFLAT;

      // which condition type is it 
      const type = newC.type

      // This is for all new fields no difference between Nested or Flat
      // if (Number(type) >= 9000 && Number(type) <= 9999) {
      if (type === 'baseField') {
        console.log('Base Field')
        return mapBaseFieldConditionFlat(newC)
        // return baseConditionNESTED(newC)
      } else if (type === 'customField') {
        console.log('Custom Field')
        return mapCustomFieldConditionFlat(newC)
      } else {
        //! this is a scenario where the type wasn't correctly set, first set the type then return the condition
        if (newC.fieldId >= 9000 && newC.fieldId <= 9999) {
          newC.type = 'baseField'
          return mapBaseFieldConditionFlat(newC)
        } else {
          newC.type = 'customField'
          return mapCustomFieldConditionFlat(newC)
        }
      }
    }

    const oldC = c as OldCondition;
    const conditionObject = baseFields?.data?.find((item: any) => item?.Meta?.Name === findFieldName(oldC.FieldData))

    if (oldC.FieldData.Fieldtype < 10) {
      return mapBaseFieldCondition(oldC, conditionObject);
    } else if (oldC.FieldData.Fieldtype > 10) {
      return mapCustomFieldCondition(oldC);
    } else {
      console.warn('Invalid Fieldtype')
      return null;
    }

  };

  // This function is used to get the new value for the form based on the fieldTypeId and equality i.e. isBetween, is, isNotBetween, isNot, and date, integer, string, toggle fields
  const getNewValueForPopulatingTheForm = (value: string[], fieldTypeId: number, equality: number) => {
    console.log(value, fieldTypeId, equality)
    if (equality === EqualityNumber.isBetween && fieldTypeId === 22) { // date field - isBetween
      if (!value) {
        return null
      }
      console.log('isBetween')
      const start = dayjs(value[0]).utc().format()
      const end = dayjs(value[1]).utc().format()
      return { start, end }
      // if the fieldTypeId is 22 then return the value as a date
    } else if (fieldTypeId === 22) { // date field - single value
      return dayjs(value[0]).utc().format()
    } else if (fieldTypeId === 16 && equality === EqualityNumber.isBetween && Array.isArray(value) && value.length === 2) { // integer field - isBetween
      return [parseInt(value[0]), parseInt(value[1])]
    } else { // any other field type
      return value
    }
  }

  const mapBaseFieldConditionFlat = (newC: NewConditionFLAT) => {
    const conditionObj = baseFields?.data?.find((item: any) => item?.Meta?.Cid === newC.fieldId)
    console.log(conditionObj)
    const baseFieldConditionFlat = {
      ...newC,
      value: getNewValueForPopulatingTheForm(newC.value, newC.fieldTypeId, parseInt(newC.equality)),
      equalityDisplay: EqualityDisplayValueArray[newC.equality as keyof typeof EqualityDisplayValueArray],
      conditionObj: conditionObj
    }
    console.log(baseFieldConditionFlat)
    return baseFieldConditionFlat
  }

  const mapCustomFieldConditionFlat = (newC: NewConditionFLAT) => {
    const conditionObj = customFields?.data?.find((item: any) => item?.Meta?.Cid === newC.fieldId)
    console.log(conditionObj)
    if (!conditionObj) {
      console.warn('Condition object not found')
      enqueueSnackbar(
        `Condition not found for field "${newC.column}". 
        Please verify this field is assigned to customer "${customers.data?.find(
          (customer: Customer) => customer.CustomerEntId === currentWorkflowRule?.Customer
        )?.EntName || 'Unknown'}" and try again.`,
        {
          variant: 'error',
        }
      )
      return null
    }
    const customFieldConditionFlat = {
      ...newC,
      value: getNewValueForPopulatingTheForm(newC.value, newC.fieldTypeId, parseInt(newC.equality)),
      equalityDisplay: EqualityDisplayValueArray[newC.equality as keyof typeof EqualityDisplayValueArray],
      conditionObj: conditionObj
    }
    console.log(customFieldConditionFlat)
    return customFieldConditionFlat
  }

  // const baseConditionNESTED = (c: NewConditionNESTED) => {
  //   const newC = c as NewConditionNESTED;

  //   const conditionObj = baseFields?.data?.find((item: any) => item?.Meta?.Cid === newC.FieldData.FieldStringV2[0].fieldId)

  //   // Check if we have a value array with length of 2 and equality is 4 (isBetween)
  //   const fieldStringV2 = newC.FieldData.FieldStringV2[0];
  //   let valueArray = fieldStringV2.value;
  //   const equality = Number(fieldStringV2.equality);

  //   // Log for debugging
  //   console.log('Processing condition values:', {
  //     valueArray,
  //     equality,
  //     fieldType: newC.FieldData.FieldType
  //   });

  //   // Handle "isBetween" case (equality === 4) with two values
  //   if (equality === EqualityNumber.isBetween && Array.isArray(valueArray) && valueArray.length === 2) {
  //     // For date or integer types, we need to ensure both values are properly formatted
  //     // This will be used in the return object below instead of just value[0]
  //     console.log('Detected isBetween condition with range:', valueArray);
  //     valueArray = [valueArray[0], valueArray[1]];
  //   }

  //   return {
  //     fieldTypeId: conditionObj?.Meta?.Cid, //! old condition ned to pass the conditionId in the patch request - 
  //     conditionId: newC.FieldData.FieldStringV2[0].conditionId, //! New condition this is 0 otherwise pass it along from api 
  //     value: equality === EqualityNumber.isBetween && Array.isArray(valueArray) && valueArray.length === 2
  //       ? valueArray
  //       : newC.FieldData.FieldStringV2[0].value[0], //!  this will have to change for isBetween      type: newC.FieldData.FieldType,
  //     column: newC.FieldData.FieldStringV2[0].column,
  //     equality: newC.FieldData.FieldStringV2[0].equality,
  //     equalityDisplay: EqualityDisplayValueArray[newC.FieldData.FieldStringV2[0].equality as keyof typeof EqualityDisplayValueArray],
  //     fieldId: newC.FieldData.FieldStringV2[0].fieldId,
  //     userId: newC.FieldData.FieldStringV2[0].userId,
  //     statusValue: newC.FieldData.FieldStringV2[0].statusValue,
  //     conditionObj: conditionObj,
  //   };
  // }

  // const mapConditionNESTED = (c: NewConditionNESTED) => {
  //   const newC = c as NewConditionNESTED;

  //   const type = newC.FieldData.FieldType

  //   const conditionObj = customFields?.data?.find((item: any) => item?.Meta?.Cid === newC.FieldData.FieldStringV2[0].fieldId)

  //   console.log({ conditionObj })

  //   // Check if we have a value array with length of 2 and equality is 4 (isBetween)
  //   const fieldStringV2 = newC.FieldData.FieldStringV2[0];
  //   let valueArray = fieldStringV2.value;
  //   const equality = Number(fieldStringV2.equality);

  //   // Log for debugging
  //   console.log('Processing condition values in mapConditionNESTED:', {
  //     valueArray,
  //     equality,
  //     fieldType: newC.FieldData.FieldType
  //   });

  //   // Handle "isBetween" case (equality === 4) with two values
  //   if (equality === EqualityNumber.isBetween && Array.isArray(valueArray) && valueArray.length === 2) {
  //     // For date or integer types, we need to ensure both values are properly formatted
  //     console.log('Detected isBetween condition with range:', valueArray);
  //     // Always use an array for value to support both single values and ranges
  //     valueArray = [valueArray[0], valueArray[1]];
  //   }

  //   const condition = {
  //     fieldTypeId: conditionObj?.Meta?.Cid,
  //     conditionId: newC.FieldData.FieldStringV2[0].conditionId,
  //     // Always use an array for value to support both single values and ranges
  //     value: equality === EqualityNumber.isBetween && Array.isArray(valueArray) && valueArray.length === 2
  //       ? valueArray
  //       : newC.FieldData.FieldStringV2[0].value[0], //!  this will have to change for isBetween
  //     // value: newC.FieldData.FieldStringV2[0].value[0], 
  //     type: newC.FieldData.FieldType,
  //     column: newC.FieldData.FieldStringV2[0].column,
  //     equality: Number(newC.FieldData.FieldStringV2[0].equality),
  //     equalityDisplay: EqualityDisplayValueArray[Number(newC.FieldData.FieldStringV2[0].equality) as keyof typeof EqualityDisplayValueArray],
  //     fieldId: newC.FieldData.FieldStringV2[0].fieldId,
  //     userId: newC.FieldData.FieldStringV2[0].userId,
  //     statusValue: newC.FieldData.FieldStringV2[0].statusValue,
  //     conditionObj: conditionObj,
  //   };
  //   console.log(condition)
  //   return condition
  // }

  const transformWorkflowRule = (rule: any): WorkflowRule => {
    if (!rule) return initialWorkflowRule;

    return {
      description: rule.Description || '',
      customer: {
        id: rule.Customer || 0,
        label: customers.data?.find((customer: Customer) => customer.CustomerEntId === rule.Customer)?.EntName || '',
      },
      matchType: rule.MatchType?.toLowerCase() === 'all' ? 'all' : 'any',
      any_conditions: rule.AnyConditions?.map(mapCondition) || [],
      all_conditions: rule.AllConditions?.map(mapCondition) || [],
      status_switch: rule.Actions?.some((action: OldEmailAction | OldAssigneeAction | OldStatusAction) => action.Actiontype === 2),
      assignee_switch: rule.Actions?.some((action: OldEmailAction | OldAssigneeAction | OldStatusAction) => action.Actiontype === 3),
      email_switch: rule.Actions?.some((action: OldEmailAction | OldAssigneeAction | OldStatusAction) => action.Actiontype === 1),
      actions: mapActions(rule.Actions) || [],
    }
  }

  const handleChangeCustomer = () => {
    console.log('Changing customer')
    // Reset form to initial state
    methods.reset({
      ...initialWorkflowRule,
      description: currentWorkflowRule?.Description || '',
      actions: mapActions(currentWorkflowRule?.Actions) || [],
      customer: {
        id: customerIdWatch,
        label: customers.data?.find((customer: Customer) => customer.CustomerEntId === customerIdWatch)?.EntName || '',
      }
    });

    // Close the modal
    setOpenCustomerChangeModal(false);

    // Show success notification
    enqueueSnackbar('Customer changed successfully. All conditions have been reset.', {
      variant: 'success',
      autoHideDuration: 3000,
    });
  }

  // Watch for customer ID changes and alert when it changes
  useEffect(() => {
    console.log('Customer ID changed:', customerIdWatch);
    console.log('Current Workflow Rule Customer:', currentWorkflowRule?.Customer);

    // Skip the check on initial load
    if (!currentWorkflowRule) {
      return;
    }

    // Only open modal when customer changes after initial load
    if (customerIdWatch !== 0 && customerIdWatch !== currentWorkflowRule?.Customer) {
      setOpenCustomerChangeModal(true);
    }
  }, [customerIdWatch, currentWorkflowRule?.Customer]);
  // Reset form with transformed data when currentWorkflowRule changes
  useEffect(() => {
    if (currentWorkflowRule) {
      const transformedData = transformWorkflowRule(currentWorkflowRule);
      methods.reset(transformedData);
      console.log(transformedData)
      console.log(methods.getValues())
    }
  }, [currentWorkflowRule]);

  // Open drawer when component mounts
  useEffect(() => {
    setIsOpen(true)
    console.log(workflowruleId)
    console.log({ currentWorkflowRule })
    if (currentWorkflowRule) {
      const transformedData = transformWorkflowRule(currentWorkflowRule);
      methods.reset(transformedData);
      console.log(transformedData)
      console.log(methods.getValues())
    }
  }, [])

  return (
    <DrawerLayout
      open={isOpen}
      onClose={handleCloseDrawer}
      anchor="right"
      keepMounted
      width={'80%'}
      Header={
        <DrawerHeader
          close={handleCloseDrawer}
          titleElement={variant === 'edit' ? (
            <>
              <Stack direction="row" >
                <Typography variant="h4" mr={2}>
                  Edit Workflow Rule
                </Typography>
                <Typography variant="subtitle2" component="p" color="text.secondary" fontSize={10} >
                  {currentWorkflowRule?.SmartRuleId}
                </Typography>
              </Stack>

            </>
          ) : 'New Workflow Rule'}
        />
      }
    >
      <FormProvider {...methods}>
        {/* @ts-ignore */}
        <Container
          maxWidth={false}
          component='form'
          onSubmit={methods.handleSubmit(handleSubmit)}
          sx={{
            overflowY: 'auto',
            mt: 5,
            height: '98%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            mb: 4,
          }}>
          <Grid size={{ xs: 12 }} marginTop={!isDesktop ? 2 : 0} height={'100%'} sx={{ overflow: 'auto', paddingBottom: '90px', mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 4, mt: 2, mb: 4, position: 'sticky', top: 0, zIndex: 1, backgroundColor: (theme: Theme) => theme.palette.background.paper, borderTop: '1px solid', borderColor: (theme: Theme) => theme.palette.divider, p: 5, }}>
              <Box sx={{ flex: 1 }}>
                <TextBox
                  id="description"
                  name="description"
                  required
                  variant="outlined"
                  label="Description"
                  size='small'
                  placeholder='Type a Title'
                  style={{ width: '100%' }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <AutoComplete
                  id="customer"
                  required
                  options={
                    customers.isPending
                      ? []
                      : customers?.data?.map((customer: any) => ({
                        id: customer.CustomerEntId,
                        label: customer.EntName,
                      })) || []
                  }
                  loading={customers.isPending}
                  autocompleteProps={{
                    id: 'customer',
                    loadingText: 'Loading Customers...',
                    size: 'small'
                  }}
                  multiple={false}
                  label="Customer"
                />
              </Box>
            </Box>
            <Tooltip
              title={customerIdWatch === 0 ? "Select a customer first to select conditions" : ""}
              placement="bottom"
              arrow
            >
              <span style={{ width: '99%' }}>
                <Accordion
                  defaultExpanded={customerIdWatch !== 0}
                  sx={{
                    mb: 8,
                    boxShadow: 'rgba(50, 50, 93, 0.025) 0px 2px 5px -1px, rgba(0, 0, 0, 0.05) 0px 1px 3px 2px;',
                    width: '100%',
                    borderRadius: 1.5,
                    '&.Mui-expanded:first-of-type': { marginTop: 0.5 },
                  }}
                  disabled={customerIdWatch === 0}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ ml: 2 }}>
                    <Typography variant="button" component="h4" sx={{ fontWeight: 'bold' }} fontSize={18}>
                      Conditions
                    </Typography>
                  </AccordionSummary>
                  <Divider />
                  <AccordionDetails sx={{ p: 4 }}>
                    <Box sx={{ mb: 4, }}>
                      <WorkflowRuleStep2 />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </span>
            </Tooltip>

            <Tooltip
              title={customerIdWatch === 0 ? "Select a customer first to select actions" : ""}
              placement="bottom"
              arrow
            >
              <span style={{ width: '99%' }}>
                <Accordion
                  defaultExpanded={customerIdWatch !== 0}
                  sx={{
                    mb: 4,
                    boxShadow: 'rgba(50, 50, 93, 0.025) 0px 2px 5px -1px, rgba(0, 0, 0, 0.05) 0px 1px 3px 2px;',
                    width: '100%',
                    borderRadius: 1.5,
                    '&.Mui-expanded:first-of-type': { marginTop: 8 },
                  }}
                  disabled={customerIdWatch === 0}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ ml: 2 }}>
                    <Typography variant="button" component="h4" sx={{ fontWeight: 'bold' }} fontSize={18}>
                      Actions
                    </Typography>
                  </AccordionSummary>
                  <Divider />
                  <AccordionDetails sx={{ p: 4 }}>
                    <WorkflowRuleStep3 />
                  </AccordionDetails>
                </Accordion>
              </span>
            </Tooltip>
          </Grid>
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 1,
              backgroundColor: (theme: Theme) => theme.palette.background.paper,
              borderTop: '1px solid',
              borderColor: (theme: Theme) => theme.palette.divider,
              // p: 2,
              pt: 4,
              display: 'flex',
              justifyContent: 'space-between',
              gap: '1rem'
            }}>
            <LoadingButton
              loading={isDeleting}
              color="error"
              startIcon={<Trash2Icon />}
              size='small'
              onClick={() => setOpenModal(true)}
              disabled={variant === 'new'}
            >
              Delete
            </LoadingButton>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={handleCloseDrawer} size='small'>
                Cancel & Close
              </Button>
              {openModal && (
                <ConfirmModal
                  open={openModal}
                  setOpen={setOpenModal}
                  title="Are you sure?"
                  content="This action will permanently delete this Workflow Rule."
                  onSuccess={handleDelete}
                  onClose={() => setOpenModal(false)}
                  cancelButtonText="Cancel"
                  confirmButtonText="Delete"
                />
              )}
              <LoadingButton disabled loading={isUpdating} variant="contained" type="submit" size='small'>
                Save
              </LoadingButton>
            </Box>
          </Box>
        </Container>
      </FormProvider>
      {openCustomerChangeModal && (
        <ConfirmModal
          open={openCustomerChangeModal}
          setOpen={setOpenCustomerChangeModal}
          title="Are you sure?"
          content="This will change the customer for this workflow rule. This will remove all conditions for this workflow rule."
          onSuccess={handleChangeCustomer}
          onClose={() => setOpenCustomerChangeModal(false)}
          cancelButtonText="Cancel"
          confirmButtonText="Change Customer"
        />
      )}
    </DrawerLayout >
  );
};

export default WorkflowRuleDrawer;